/**
 * useRuntimeVersions — §22 single source for per-runtime build version +
 * skew, consumed by RuntimeOverview / RuntimeSelector / OrgUsers /
 * RuntimeFleet.
 *
 * Optional `orgId` arg → server-side `?orgId=` filter (RuntimeFleet's
 * per-tenant view). Omitted ⇒ all orgs (every other consumer; unchanged
 * default). Changing it refetches immediately (see the refresh effect —
 * usePolling's stable doFetch would otherwise only pick the new filter
 * up on the next 60s tick).
 *
 * Consumes two endpoints owned elsewhere (we never compute skew):
 *  - `GET /api/platform/runtimes/versions` — operator BATCH endpoint
 *    (SHIPPED 2026-05-19 by the lexserver/LexIDE track; the feature
 *    gate below is retained as defensive degrade-not-crash).
 *  - `GET /api/platform/runtime/target-version` — ships today; the
 *    target tag is only a DISPLAY label for the `behind` badge.
 *
 * Feature gate (the batch endpoint may be absent): the fetcher never
 * throws — it returns `available:false` when the batch call fails with
 * either (a) a PlatformApiError 404 (JSON-enveloped 404), OR (b) any
 * non-PlatformApiError (Hono's default plain-text 404 → SyntaxError in
 * `res.json()`, or a network error). A PlatformApiError with another
 * status (401/403/500) keeps `available:true` and surfaces `error` so
 * the screen shows a real failure rather than silently hiding. A
 * target-version failure alone never flips the gate.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { usePlatformApi } from "./usePlatformApi";
import { usePolling } from "./usePolling";
import { PlatformApiError } from "@/lib/platform-api";
import type { RuntimeVersionEntry } from "@/types/platform";

const POLL_INTERVAL_MS = 60_000;

interface BatchResult {
  available: boolean;
  byUserId: Map<string, RuntimeVersionEntry>;
  error: string | null;
}

export interface UseRuntimeVersionsResult {
  /** Per-runtime entry keyed by userId (empty until first fetch / when gated). */
  byUserId: Map<string, RuntimeVersionEntry>;
  /** Platform target tag for the `behind` badge label; null when unknown. */
  targetVersion: string | null;
  /** False → batch endpoint not live/reachable: screens omit the version UI. */
  available: boolean;
  /** Set only when the endpoint exists but genuinely faulted (HTTP 500). */
  error: string | null;
  loading: boolean;
  /** True until the FIRST poll resolves — distinguishes "still loading"
   *  from "resolved unavailable" so the gate notice never flashes. */
  initializing: boolean;
  /** Force an immediate refetch (e.g. right after a per-row Repair). */
  refresh: () => void;
}

export function useRuntimeVersions(orgId?: string): UseRuntimeVersionsResult {
  const api = usePlatformApi();
  const [targetVersion, setTargetVersion] = useState<string | null>(null);

  // target-version changes only on a platform redeploy → fetch once on
  // mount, not on every 60s poll. A failure here does NOT gate the
  // feature; the badge falls back to bare "outdated" (never blank).
  useEffect(() => {
    let cancelled = false;
    api
      .getRuntimeTargetVersion()
      .then((r) => {
        if (cancelled) return;
        const tv = r.data.targetVersion;
        setTargetVersion(tv && tv !== "unknown" ? tv : null);
      })
      .catch(() => {
        if (!cancelled) setTargetVersion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const fetcher = useCallback(async (): Promise<BatchResult> => {
    try {
      const res = await api.listRuntimeVersions(orgId);
      // Insertion order preserves the server's orgId→containerName
      // ordering (Map iteration is insertion-ordered) — no client sort.
      const byUserId = new Map<string, RuntimeVersionEntry>();
      for (const e of res.data) byUserId.set(e.userId, e);
      return { available: true, byUserId, error: null };
    } catch (err) {
      if (err instanceof PlatformApiError) {
        // 404: route absent (JSON-enveloped). 401/403: lexserver's
        // jwtAuthMiddleware runs on /api/* BEFORE route-matching, so an
        // unbuilt endpoint with a missing/expired token returns 401 —
        // NOT 404. Pre-endpoint we cannot distinguish "route absent +
        // bad token → 401" from "route absent + good token → 404"; both
        // mean the feature is unusable, so both gate gracefully rather
        // than surfacing a scary "Invalid or expired token" banner on a
        // page that is simply not live yet. Only a 500 (endpoint exists,
        // genuine server fault) is surfaced as a real error.
        if (err.status === 404 || err.status === 401 || err.status === 403) {
          return { available: false, byUserId: new Map(), error: null };
        }
        return { available: true, byUserId: new Map(), error: err.message };
      }
      // Non-PlatformApiError: Hono default text 404 → res.json() SyntaxError,
      // or a network error → treat as "feature not live yet".
      return { available: false, byUserId: new Map(), error: null };
    }
  }, [api, orgId]);

  const { data, loading, refresh } = usePolling(fetcher, POLL_INTERVAL_MS);

  // usePolling keeps a stable doFetch + a fetcherRef it updates silently;
  // a new fetcher identity (orgId changed) does NOT re-run its effect, so
  // without this the filter change would only apply on the next 60s tick.
  // Skip the mount run — usePolling already does the initial fetch.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    refresh();
  }, [orgId, refresh]);

  return {
    byUserId: data?.byUserId ?? new Map<string, RuntimeVersionEntry>(),
    targetVersion,
    available: data?.available ?? false,
    error: data?.error ?? null,
    loading,
    // data is null ONLY before the first poll resolves — the gate path
    // returns a (non-null) BatchResult, never null. So data===null ⟺
    // "first fetch in flight" ⟺ initializing.
    initializing: data === null,
    /** Force an immediate refetch (e.g. right after a per-row Repair). */
    refresh,
  };
}
